import type { Intersect } from '../../common/types';
import type { Collection, SerializedVertexExport, Vertex } from '../interfaces/vertices/util';
import type { PickEdges } from '../interfaces/vertices/util/common';
import { omit } from 'lodash-es';


const keys =  ['_id',  'createdAt', 'updatedAt', 'lastModifiedBy', 'CONTEXT', 'domain', 'derivedParentGroups'] as const;
export type InternalKeysOmitted<V extends object> = Omit<Intersect<V>, typeof keys[number]>;

export type SerializedEdgesOnlyVertexExport<
  V extends Vertex<Collection>
> = {
  [K in keyof SerializedVertexExport<V> as K extends keyof InternalKeysOmitted<PickEdges<V>> ? K : never]: SerializedVertexExport<V>[K]
};

/**
 *  Used only as `satisfies` to ensure that no edge property is missing.
 */
export type SerializedEdgesShape<V extends object> = Record<keyof Omit<V, 'domain'>, unknown>;

/**
 * Returns a copy of the vertex with internal keys omitted, such as `_id`, `createdAt`, `updatedAt`, etc.
 * The output type also omits all edges for ease of use.
 */
export function omitEdgesAndInternalProperties <
  V           extends object,
  EdgesRecord extends object
> (v: V, edgesRecord: EdgesRecord) {
  const a = omit(v, Object.keys(edgesRecord)) as unknown as Omit<V, keyof EdgesRecord>;
  return omit(a, keys) as unknown as InternalKeysOmitted<typeof a>;
}
